import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Link, useParams } from 'react-router-dom';
import { useStores } from '../../hooks/use-stores';
import {
    Block,
    FlexDiv,
    SpaceBetweenDiv,
    TextDefault,
    TextLead,
    Heading2,
    Image,
    BigModal,
    ThemedButton,
} from '@awarego-ui/components';
import { ButtonBase } from '@mui/material';
import LogoUpload from '../../components/company/logo-upload';
import FaviconUpload from '../../components/company/favicon-upload';
import ImageDisplay from '../../components/company/ImageDisplay';
import ThemedSpinner from '../../components/themed/ThemedSpinner';
import { extractErrorMessage } from '../../utils/helpers';

function PartnerAppearance() {
    const { partnerId } = useParams();
    const { commonStore, partnerStore } = useStores();
    const [showLogoModal, setShowLogoModal] = useState(false);
    const [showFaviconModal, setShowFaviconModal] = useState(false);
    const {logoUrl, faviconUrl} = partnerStore;

    const partnerName = partnerStore.currentPartner?.name || 'Partner';

    useEffect(() => {
        partnerStore.loadSettings(partnerId);
    }, [partnerId, partnerStore]);

    const handleUploadLogo = async (formData) => {
        try {
            await partnerStore.uploadLogo(partnerId, formData);
            commonStore.success('Logo updated!');
        } catch (e) {
            commonStore.error(extractErrorMessage(e) || 'Failed to upload logo');
        }
    };

    const handleRemoveLogo = async () => {
        try {
            await partnerStore.removeLogo(partnerId);
            commonStore.success('Logo removed!');
        } catch (e) {
            commonStore.error(extractErrorMessage(e) || 'Failed to remove logo');
        }
    };

    const handleUploadFavicon = async (formData) => {
        try {
            await partnerStore.uploadFavicon(partnerId, formData);
            commonStore.success('Favicon updated!');
        } catch (e) {
            commonStore.error(
                extractErrorMessage(e) || 'Failed to upload favicon'
            );
        }
    };

    const handleRemoveFavicon = async () => {
        try {
            await partnerStore.removeFavicon(partnerId);
            commonStore.success('Favicon removed!');
        } catch (e) {
            commonStore.error(
                extractErrorMessage(e) || 'Failed to remove favicon'
            );
        }
    };

    const spinnerFooter = (
        <div style={{ marginTop: '-20px', marginBottom: '-20px' }}>
            <ThemedSpinner size={50} />
        </div>
    );

    return (
        <>
            <Link to={`/partner/${partnerId}/settings`}>
                <div className="navigation back-button">
                    Back to partner settings
                </div>
            </Link>

            <FlexDiv>
                <Block>
                    <FlexDiv column>
                        <Heading2>Customize appearance</Heading2>
                        <TextDefault>
                            Change the appearance of {partnerName} for
                            your companies.
                        </TextDefault>
                    </FlexDiv>

                    <SpaceBetweenDiv bottomBorder py={16} mt={8}>
                        <FlexDiv column mr={16}>
                            <TextLead bold>Partner Logo</TextLead>
                            <TextDefault>
                                Set a custom logo that will be shown on
                                your companies LMS when they sign in.
                            </TextDefault>
                        </FlexDiv>
                        <ButtonBase
                            onClick={() => setShowLogoModal(true)}
                            disableRipple={true}
                            style={{ flexDirection: 'column' }}
                        >
                            {logoUrl ? (
                                <Image
                                    width={150}
                                    src={logoUrl}
                                    alt="Partner logo"
                                />
                            ) : (
                                <Image
                                    width={50}
                                    src="/static/img/logo-image.svg"
                                    alt="Add your logo"
                                />
                            )}
                            <TextDefault themed bold>
                                Change
                            </TextDefault>
                        </ButtonBase>
                    </SpaceBetweenDiv>

                    <SpaceBetweenDiv bottomBorder py={16}>
                        <FlexDiv column mr={16}>
                            <TextLead bold>Favicon</TextLead>
                            <TextDefault>
                                Small icon of the website shown in the browser tab for
                                your companies.
                            </TextDefault>
                        </FlexDiv>
                        <ButtonBase
                            onClick={() => setShowFaviconModal(true)}
                            disableRipple={true}
                            style={{ flexDirection: 'column' }}
                        >
                            {faviconUrl ? (
                                <Image
                                    width={32}
                                    src={faviconUrl}
                                    alt="Favicon"
                                    style={{ imageRendering: 'pixelated' }}
                                />
                            ) : (
                                <Image
                                    width={32}
                                    src="/static/img/logo-image.svg"
                                    alt="Add favicon"
                                />
                            )}
                            <TextDefault themed bold>
                                Change
                            </TextDefault>
                        </ButtonBase>
                    </SpaceBetweenDiv>

                    <SpaceBetweenDiv bottomBorder py={16} mb={24}>
                        <FlexDiv column mr={16}>
                            <TextLead bold>Colors</TextLead>
                            <TextDefault>
                                Changing partner colors will is coming soon!
                            </TextDefault>
                        </FlexDiv>
                    </SpaceBetweenDiv>
                </Block>

                <Block ml={20}>
                    <Heading2>Preview</Heading2>
                    <TextDefault>
                        This is how the partner logo and favicon will appear:
                    </TextDefault>
                    <FlexDiv mt={24} column alignCenter>
                        {logoUrl ? (
                            <Image
                                src={logoUrl}
                                width={200}
                                alt="Logo preview"
                            />
                        ) : (
                            <TextDefault lighter>No logo set</TextDefault>
                        )}
                        {faviconUrl && (
                            <FlexDiv mt={16} alignCenter gap={8}>
                                <Image
                                    src={faviconUrl}
                                    width={32}
                                    alt="Favicon preview"
                                    style={{ imageRendering: 'pixelated' }}
                                />
                                <TextDefault lighter>Favicon</TextDefault>
                            </FlexDiv>
                        )}
                    </FlexDiv>
                </Block>

                <BigModal
                    isOpen={showLogoModal}
                    onRequestClose={() => setShowLogoModal(false)}
                    heading="Change logo"
                    modalContent={
                        logoUrl ? (
                            <ImageDisplay
                                src={logoUrl}
                                alt="Partner logo"
                                onRemove={handleRemoveLogo}
                                removing={partnerStore.removingLogo}
                                maxWidth="280px"
                                maxHeight="200px"
                            />
                        ) : (
                            <LogoUpload
                                onLogoSelected={handleUploadLogo}
                                uploadingLogo={partnerStore.uploadingLogo}
                            />
                        )
                    }
                    modalFooterContent={
                        partnerStore.uploadingLogo ? (
                            spinnerFooter
                        ) : (
                            <ThemedButton
                                mode="primary"
                                disabled={partnerStore.removingLogo}
                                text="Done"
                                $airy
                                onClick={() => setShowLogoModal(false)}
                            />
                        )
                    }
                />

                <BigModal
                    isOpen={showFaviconModal}
                    onRequestClose={() => setShowFaviconModal(false)}
                    heading="Change favicon"
                    modalContent={
                        faviconUrl ? (
                            <ImageDisplay
                                src={faviconUrl}
                                alt="Partner favicon"
                                onRemove={handleRemoveFavicon}
                                removing={partnerStore.removingFavicon}
                                maxWidth="64px"
                                maxHeight="64px"
                                pixelated
                            />
                        ) : (
                            <FaviconUpload
                                onFaviconSelected={handleUploadFavicon}
                                uploadingFavicon={
                                    partnerStore.uploadingFavicon
                                }
                            />
                        )
                    }
                    modalFooterContent={
                        partnerStore.uploadingFavicon  ? (
                            spinnerFooter
                        ) : (
                            <ThemedButton
                                mode="primary"
                                text="Done"
                                disabled={partnerStore.removingFavicon}
                                $airy
                                onClick={() => setShowFaviconModal(false)}
                            />
                        )
                    }
                />
            </FlexDiv>
        </>
    );
}

export default observer(PartnerAppearance);
